import React, { useEffect, useState, useCallback } from "react";
import { Col, Row, Select, Tag, message, Button, Modal, Input, Dropdown, Drawer } from "antd";
import { DataGrid, GridOverlay } from "@mui/x-data-grid";
import { MoreOutlined, SearchOutlined } from "@ant-design/icons";
import { Terminal, Copy } from "lucide-react";
import axios from "axios";

// Import your constants or environment variables
import { AUTH_TOKEN, REACT_APP_API, ORG_ID, DOMAIN_ID } from "../../../constants";
// import { formatTimeByTimezone } from "../../../common/formatTimebyTimezone"; // Not used in current snippet
import MySqlTerminal from "./MySqlTerminal";

// Custom "No Rows" overlay for the DataGrid
const CustomNoRowsOverlay = () => (
  <GridOverlay>
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        color: "#666",
      }}
    >
      <span>No Data</span>
    </div>
  </GridOverlay>
);

const PAGE_SIZE = 8;

const ConnectionsScreen = ({ showNotification }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  // Filters
  const [selectedDatabase, setSelectedDatabase] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedHost, setSelectedHost] = useState(null);
  const [selectedPrivilege, setSelectedPrivilege] = useState(null);
  const [searchValue, setSearchValue] = useState("");

  // Dropdown filter lists
  const [databaseList, setDatabaseList] = useState([]);
  const [userList, setUserList] = useState([]);

  // Actual connections displayed in the DataGrid
  const [connectionData, setConnectionData] = useState([]);

  // Pagination
  const [paginationModel, setPaginationModel] = useState({
    page: 0, // 0-based index
    pageSize: PAGE_SIZE,
  });

  // For user notifications
  const [notification, contextHolder] = message.useMessage();

  // For connection string modal
  const [isConnectionModalOpen, setIsConnectionModalOpen] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState(null);

  // For terminal drawer
  const [isTerminalDrawerOpen, setIsTerminalDrawerOpen] = useState(false);
  const [selectedTerminalConnection, setSelectedTerminalConnection] = useState(null);

  // State for handling loading during connection string refresh attempt
  const [isProcessingAction, setIsProcessingAction] = useState(false);
  const [currentActionConnectionId, setCurrentActionConnectionId] = useState(null);

  useEffect(() => {
    fetchFilterLists();
  }, []);

  useEffect(() => {
    fetchConnectionData();
  }, [
    selectedDatabase,
    selectedUser,
    selectedHost,
    selectedPrivilege,
    paginationModel,
    searchValue,
  ]);

  const fetchFilterLists = async () => {
    try {
      const payload = { orgId: ORG_ID(), tenantId: DOMAIN_ID() };
      const headers = { "X-Authorization": AUTH_TOKEN(), withCredentials: true };
      const [dbRes, userRes] = await Promise.all([
        axios.post(`${REACT_APP_API}/api/v1/databaseService/getUniqueDatabasenames`, payload, {
          headers,
        }),
        axios.post(`${REACT_APP_API}/api/v1/databaseService/getUniqueUsernames`, payload, {
          headers,
        }),
      ]);
      setDatabaseList(dbRes.data?.db_names || []);
      setUserList(userRes.data?.usernames || []);
    } catch (error) {
      console.error("Error fetching filter lists:", error);
      notification.error({ content: "Unable to fetch database/user lists" });
    }
  };

  const fetchConnectionData = () => {
    const filters = [];
    if (selectedHost) filters.push({ filterParameter: "hostname", filterValue: selectedHost });
    if (selectedPrivilege)
      filters.push({ filterParameter: "privilege", filterValue: selectedPrivilege });
    if (selectedUser) filters.push({ filterParameter: "user", filterValue: selectedUser });
    if (selectedDatabase)
      filters.push({ filterParameter: "database_name", filterValue: selectedDatabase });
    // Note: Original code had searchValue filter on "connection_string", adjust if "hostname" or other fields are more appropriate for general search
    if (searchValue) filters.push({ filterParameter: "hostname", filterValue: searchValue });

    const payload = {
      orgId: ORG_ID(),
      tenantId: DOMAIN_ID(),
      filters,
      requestId: "",
      page_id: paginationModel.page + 1,
      limit: paginationModel.pageSize,
    };

    setIsLoading(true);
    const userId = localStorage.getItem("userId");
    const email = localStorage.getItem("UserName");

    axios
      .post(`${REACT_APP_API}/api/v1/databaseService/listConnections`, payload, {
        headers: {
          "X-Authorization": AUTH_TOKEN(),
          userid: userId,
          email: email,
          withCredentials: true,
        },
      })
      .then((res) => {
        const dataArray = res?.data?.data || [];
        const mapped = dataArray.map((item, index) => ({
          ...item,
          id: item.id || `${item.database_name}-${item.ip_address}-${index}`, // Ensure unique ID
        }));
        setConnectionData(mapped);
        setTotalCount(res?.data?.total_count || 0);
      })
      .catch((err) => {
        console.error("Error fetching connections:", err);
        notification.error({ content: "Unable to fetch connections" });
      })
      .finally(() => setIsLoading(false));
  };

  const resetPageAndSetState = (setter) => (value) => {
    setter(value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  // Modified to search 'hostname' as per filter construction in fetchConnectionData
  const handleSearch = (value) => {
    setSearchValue(value); // This will be used as 'hostname' filter
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const getMenuItems = (connection) => [
    { key: "connect", label: "Use Web Console", icon: <Terminal size={16} /> },
    { key: "copy", label: "Use Direct Access", icon: <Copy size={16} /> },
  ];

  const handleMenuClick = async (connection, actionKey) => {
    setCurrentActionConnectionId(connection.id);
    setIsProcessingAction(true);
    const actionNotificationKey = `freshConn-${connection.id || Date.now()}`; // Unique key for notification
    notification.info({
      content: "Attempting to refresh connection details...",
      key: actionNotificationKey,
      duration: 0,
    });

    let freshConnectionString = connection.connection_string; // Fallback to existing string
    let successfullyRefreshed = false;

    try {
      // Attempt to re-fetch the specific connection using its ID.
      // This assumes the listConnections API supports filtering by 'id'.
      // If not, you'll need to use other available and specific filterParameters.
      const itemSpecificFilters = [{ filterParameter: "id", filterValue: connection.id }];

      // If filtering by "id" is not supported by your API, use other fields:
      // const itemSpecificFilters = [
      //   { filterParameter: "database_name", filterValue: connection.database_name },
      //   { filterParameter: "ip_address", filterValue: connection.ip_address }
      //   // Add other filters if needed to make it unique enough
      // ];

      const payload = {
        orgId: ORG_ID(),
        tenantId: DOMAIN_ID(),
        filters: itemSpecificFilters,
        requestId: "",
        page_id: 1,
        limit: 1, // We expect only one item if filtering by a unique ID
      };

      const userId = localStorage.getItem("userId");
      const email = localStorage.getItem("UserName");

      const response = await axios.post(
        `${REACT_APP_API}/api/v1/databaseService/listConnections`,
        payload,
        {
          headers: {
            "X-Authorization": AUTH_TOKEN(),
            userid: userId,
            email: email,
            withCredentials: true,
          },
        }
      );

      if (response.data?.data && response.data.data.length > 0) {
        // Find the item, ideally it's the first and only one
        const refreshedItem = response.data.data.find((item) => item.id === connection.id);
        if (refreshedItem && refreshedItem.connection_string) {
          freshConnectionString = refreshedItem.connection_string;
          successfullyRefreshed = true;
          notification.success({
            content: "Connection details refreshed.",
            key: actionNotificationKey,
            duration: 3,
          });
        } else {
          notification.warn({
            content: "Refresh inconclusive. Using last known string (may be expired).",
            key: actionNotificationKey,
            duration: 5,
          });
        }
      } else {
        notification.warn({
          content:
            "Failed to refresh details (item not found). Using last known string (may be expired).",
          key: actionNotificationKey,
          duration: 5,
        });
      }
    } catch (error) {
      console.error("Error refreshing connection string:", error);
      notification.error({
        content: "Error during refresh. Using last known string (may be expired).",
        key: actionNotificationKey,
        duration: 5,
      });
    }

    const updatedConnection = { ...connection, connection_string: freshConnectionString };

    if (actionKey === "connect") {
      setSelectedTerminalConnection(updatedConnection);
      setIsTerminalDrawerOpen(true);
    } else if (actionKey === "copy") {
      setSelectedConnection(updatedConnection);
      setIsConnectionModalOpen(true);
    }
    setIsProcessingAction(false);
    setCurrentActionConnectionId(null);
  };

  const handleCopyConnectionString = () => {
    if (selectedConnection?.connection_string) {
      navigator.clipboard.writeText(selectedConnection.connection_string);
      notification.success({ content: "Connection string copied!" });
      setIsConnectionModalOpen(false);
    } else {
      notification.error({ content: "No connection string to copy." });
    }
  };

  const handleTerminalExit = useCallback(() => {
    setIsTerminalDrawerOpen(false);
    setSelectedTerminalConnection(null);
    notification.info({ content: "Terminal session closed." });
  }, [notification]);

  const handleDrawerClose = useCallback(() => {
    setIsTerminalDrawerOpen(false);
    setSelectedTerminalConnection(null);
  }, []);

  const columns = [
    { headerName: "Hostname", field: "ip_address", flex: 1 },
    { headerName: "Database", field: "database_name", flex: 1.5 },
    {
      headerName: "DB Type", // New Column for DB Type
      field: "dbtype",
      flex: 0.8,
      renderCell: (params) => (params.value ? <Tag>{params.value.toUpperCase()}</Tag> : null),
    },
    {
      headerName: "Users",
      field: "users",
      flex: 1.5,
      renderCell: (params) =>
        Array.isArray(params.value) && params.value.map((user) => <Tag key={user}>{user}</Tag>),
    },
    {
      headerName: "Privileges",
      field: "user_privilege",
      flex: 1.5,
      renderCell: (params) =>
        Array.isArray(params.value) &&
        params.value.map((priv) => {
          let color = "blue";
          if (priv === "READ") color = "green";
          else if (priv === "WRITE") color = "orange";
          else if (priv === "EXECUTE") color = "purple";
          return (
            <Tag key={priv} color={color}>
              {priv}
            </Tag>
          );
        }),
    },
    {
      headerName: "Actions",
      field: "actions",
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Dropdown
          menu={{
            items: getMenuItems(params.row),
            onClick: ({ key }) => handleMenuClick(params.row, key),
          }}
          placement="bottomRight"
          trigger={["click"]}
          disabled={isProcessingAction && currentActionConnectionId === params.row.id}
        >
          <Button
            type="primary"
            loading={isProcessingAction && currentActionConnectionId === params.row.id}
          >
            Connect
          </Button>
        </Dropdown>
      ),
    },
  ];

  const appliedFilters = [];
  if (selectedDatabase)
    appliedFilters.push({ key: "Database", display: `Database: ${selectedDatabase}` });
  if (selectedUser) appliedFilters.push({ key: "User", display: `User: ${selectedUser}` });
  if (selectedHost) appliedFilters.push({ key: "Host", display: `Host: ${selectedHost}` });
  if (selectedPrivilege)
    appliedFilters.push({ key: "Privilege", display: `Privilege: ${selectedPrivilege}` });
  if (searchValue)
    appliedFilters.push({ key: "Search", display: `Search (Hostname): ${searchValue}` });

  return (
    <>
      {contextHolder}
      <div className="content-container">
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <h2 className="title">Database Connections</h2>
            <p className="subtitle">Manage and monitor your database connections</p>
          </Col>
        </Row>

        <div className="filter-section">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6} lg={4}>
              <div className="filter-item">
                <label className="filter-label">Database</label>
                <Select
                  showSearch
                  allowClear
                  placeholder="Select Database"
                  style={{ width: "100%" }}
                  value={selectedDatabase}
                  onChange={resetPageAndSetState(setSelectedDatabase)}
                  filterOption={(input, option) =>
                    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                  options={databaseList.map((db) => ({ label: db, value: db }))}
                />
              </div>
            </Col>
            <Col xs={24} sm={12} md={6} lg={4}>
              <div className="filter-item">
                <label className="filter-label">User</label>
                <Select
                  showSearch
                  allowClear
                  placeholder="Select User"
                  style={{ width: "100%" }}
                  value={selectedUser}
                  onChange={resetPageAndSetState(setSelectedUser)}
                  filterOption={(input, option) =>
                    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                  options={userList.map((user) => ({ label: user, value: user }))}
                />
              </div>
            </Col>
            <Col xs={24} sm={12} md={6} lg={4}>
              <div className="filter-item">
                <label className="filter-label">Host</label>
                <Input
                  allowClear
                  placeholder="Search by Hostname"
                  value={searchValue} // Changed from selectedHost to searchValue for global search
                  onChange={(e) => handleSearch(e.target.value)} // Use handleSearch for global search
                />
              </div>
            </Col>
            <Col xs={24} sm={12} md={6} lg={4}>
              <div className="filter-item">
                <label className="filter-label">Privilege</label>
                <Select
                  showSearch
                  allowClear
                  placeholder="Select Privilege"
                  style={{ width: "100%" }}
                  value={selectedPrivilege}
                  onChange={resetPageAndSetState(setSelectedPrivilege)}
                  options={[
                    { label: "READ", value: "READ" },
                    { label: "WRITE", value: "WRITE" },
                    { label: "EXECUTE", value: "EXECUTE" },
                  ]}
                />
              </div>
            </Col>
            {/* Removed the dedicated search button as search is now triggered by Hostname input onChange and other filters */}
          </Row>
        </div>

        {appliedFilters.length > 0 && (
          <div className="applied-filters">
            <p className="filter-tags-label">Applied Filters</p>
            <div className="filter-tags">
              {appliedFilters.map(({ key, display }) => (
                <Tag
                  key={key}
                  closable
                  onClose={() => {
                    if (key === "Database") resetPageAndSetState(setSelectedDatabase)(null);
                    else if (key === "User") resetPageAndSetState(setSelectedUser)(null);
                    else if (key.startsWith("Search")) handleSearch(""); // Clear global search
                    else if (key === "Host")
                      resetPageAndSetState(setSelectedHost)(
                        null
                      ); // Retain specific host filter logic if needed elsewhere
                    else if (key === "Privilege") resetPageAndSetState(setSelectedPrivilege)(null);
                  }}
                  className="filter-tag"
                >
                  {display}
                </Tag>
              ))}
            </div>
          </div>
        )}

        <div className="data-grid-container" style={{ height: 550, width: "100%" }}>
          <DataGrid
            components={{ NoRowsOverlay: CustomNoRowsOverlay }}
            rows={connectionData}
            columns={columns}
            paginationMode="server"
            rowCount={totalCount}
            loading={isLoading} // Main loading for the grid
            className="custom-data-grid"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[PAGE_SIZE, 10, 20]}
          />
        </div>
      </div>

      <Modal
        title="Connection Details"
        open={isConnectionModalOpen}
        onCancel={() => setIsConnectionModalOpen(false)}
        centered
        width={1200}
        className="connection-modal"
        footer={[
          <Button
            key="copy"
            icon={<Copy size={16} />}
            type="primary"
            onClick={handleCopyConnectionString}
            className="copy-button"
          >
            Copy
          </Button>,
          <Button
            key="close"
            onClick={() => setIsConnectionModalOpen(false)}
            className="close-button"
          >
            Close
          </Button>,
        ]}
      >
        {selectedConnection && (
          <div className="connection-details">
            <h3>Instructions</h3>
            <p>
              Please copy and paste the following command to establish a connection to the database.
              <br />
              <strong>Note:</strong> A recent attempt was made to generate this access string. If the
              connection failed or was inconclusive, the string might be short-lived or expired.
            </p>
            <pre
              className="connection-string"
              style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}
            >
              {selectedConnection.connection_string}
            </pre>
          </div>
        )}
      </Modal>

      <Drawer
        title="Web Console"
        placement="bottom"
        onClose={handleDrawerClose}
        open={isTerminalDrawerOpen}
        height="80%"
        destroyOnClose={true}
        rootClassName="terminal-drawer"
      >
        {selectedTerminalConnection && isTerminalDrawerOpen && (
          <MySqlTerminal
            connectionString={selectedTerminalConnection.connection_string}
            onTerminalExit={handleTerminalExit}
          />
        )}
      </Drawer>

      <style jsx>{`
        /* Styles remain the same */
        .content-container {
          padding: 24px;
          background: #fff;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }
        .title {
          font-size: 24px;
          font-weight: 600;
          color: #1a1a1a;
          margin: 0;
        }
        .subtitle {
          font-size: 14px;
          color: #666;
          margin: 4px 0 0;
        }
        .filter-section {
          background: #f8f9fa;
          padding: 16px;
          border-radius: 8px;
          margin-bottom: 24px;
        }
        .filter-item {
          margin-bottom: 8px;
        }
        .filter-label {
          display: block;
          margin-bottom: 8px;
          color: #666;
          font-size: 14px;
        }
        .applied-filters {
          margin-bottom: 16px;
        }
        .filter-tags-label {
          color: #666;
          margin-bottom: 8px;
        }
        .filter-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }
        .filter-tag {
          background: #e6f7ff;
          border-color: #91d5ff;
          color: #1890ff;
        }
        .data-grid-container {
          background: #fff;
          border-radius: 8px;
          overflow: hidden;
        }
        .custom-data-grid {
          border: none !important;
        }
        .connection-modal :global(.ant-modal-content) {
          border-radius: 8px;
        }
        .connection-details {
          padding: 24px;
          background: #f8f9fa;
          border-radius: 8px;
        }
        .connection-details h3 {
          margin-bottom: 16px;
          color: #1a1a1a;
        }
        .connection-details p {
          color: #666;
          margin-bottom: 16px;
        }
        .connection-string {
          background: #fff;
          padding: 16px;
          border-radius: 4px;
          border: 1px solid #e8e8e8;
          overflow-x: auto;
          font-family: monospace;
          font-size: 14px;
          line-height: 1.5;
        }
        .copy-button {
          background: #1890ff;
          border-color: #1890ff;
        }
        .close-button {
          margin-left: 8px;
        }
        .terminal-drawer :global(.ant-drawer-content-wrapper) {
          border-top-left-radius: 8px;
          border-top-right-radius: 8px;
        }
        .terminal-drawer :global(.ant-drawer-body) {
          padding: 0;
          height: 100%;
        }
      `}</style>
    </>);
};

export default ConnectionsScreen;
